import { useEffect, useMemo, useState } from "react";
import { useNotification } from "../context/NotificationContext";
import { PageHeader, Card, SectionTitle, StatusRow, CodeBlock } from "../components/UI";
import * as api from "../services/dynamicPriceApi";

const companyId = localStorage.getItem("companyid") || 1;
const apiUrl = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
const BASE = `${apiUrl}/dynamic-price/integration`;

const CH_CFG = {
  PMS: { color: "#6d28d9", bg: "#f5f3ff", border: "#c4b5fd" },
  BE: { color: "#0369a1", bg: "#e0f2fe", border: "#7dd3fc" },
  OTA: { color: "#be185d", bg: "#fdf2f8", border: "#f9a8d4" },
};

const DEFAULT_COMMISSIONS = { PMS: 0, BE: 0, OTA: 18 };

const STEPS = [
  { n: 1, label: "Connect", sub: "API key + vendor pushes /setup" },
  { n: 2, label: "Map", sub: "Rooms + Rate Plans" },
  { n: 3, label: "Sync History", sub: "Bookings + rate_info validation" },
  { n: 4, label: "Go Live", sub: "Webhook / daily push + fallback" },
];

function ChannelBadge({ ch }) {
  const c = CH_CFG[ch] || { color: "#64748b", bg: "#f8fafc", border: "#e2e8f0" };
  return (
    <span style={{
      fontSize: 10,
      fontWeight: 800,
      letterSpacing: 0.5,
      color: c.color,
      background: c.bg,
      border: `1.5px solid ${c.border}`,
      borderRadius: 5,
      padding: "3px 9px",
      whiteSpace: "nowrap",
    }}>{ch}</span>
  );
}


function ReasoningHint({ reasoning }) {
  const [open, setOpen] = useState(false);

  const hasReasoning = String(reasoning || "").trim().length > 0;

  if (!hasReasoning) {
    return (
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          fontSize: 11,
          color: "#94a3b8",
          fontWeight: 700,
          whiteSpace: "nowrap",
        }}
      >
        <span style={{ fontSize: 12 }}>🛈</span>
        No reasoning
      </span>
    );
  }

  return (
    <div
      style={{ position: "relative", display: "inline-block" }}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        aria-label="View AI reasoning"
        title="View AI reasoning"
        style={{
          border: "1px solid #dbeafe",
          background: "#eff6ff",
          color: "#1d4ed8",
          borderRadius: 999,
          padding: "4px 8px",
          fontSize: 11,
          fontWeight: 800,
          display: "inline-flex",
          alignItems: "center",
          gap: 5,
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <span style={{ fontSize: 12 }}>🤖</span>
        <span>Reasoning</span>
      </button>

      {open && (
        <div
          role="tooltip"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            zIndex: 50,
            top: "calc(100% + 8px)",
            left: 0,
            width: 340,
            maxWidth: "min(340px, 70vw)",
            background: "#0f172a",
            color: "#e2e8f0",
            border: "1px solid #1e293b",
            borderRadius: 10,
            padding: "10px 12px",
            boxShadow: "0 10px 30px rgba(2,6,23,0.35)",
            fontSize: 12,
            lineHeight: 1.5,
            fontWeight: 500,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: 0.8,
              textTransform: "uppercase",
              color: "#93c5fd",
              marginBottom: 6,
            }}
          >
            AI Reasoning
          </div>
          <div style={{ whiteSpace: "normal" }}>{reasoning}</div>

          {/* arrow */}
          <div
            style={{
              position: "absolute",
              top: -6,
              left: 18,
              width: 10,
              height: 10,
              background: "#0f172a",
              borderLeft: "1px solid #1e293b",
              borderTop: "1px solid #1e293b",
              transform: "rotate(45deg)",
            }}
          />
        </div>
      )}
    </div>
  );
}

function StepBar({ current }) {
  return (
    <div style={{ display: "flex", alignItems: "center", marginBottom: 28, flexWrap: "wrap", rowGap: 10 }}>
      {STEPS.map((s, i) => {
        const done = s.n < current;
        const active = s.n === current;
        return (
          <div key={s.n} style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 220 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
              <div style={{
                width: 34, height: 34, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 13, fontWeight: 800,
                background: done ? "#16a34a" : active ? "#2563eb" : "#e2e8f0",
                color: done || active ? "#fff" : "#94a3b8",
                boxShadow: active ? "0 0 0 4px rgba(37,99,235,0.18)" : "none",
              }}>{done ? "✓" : s.n}</div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 800, color: active ? "#2563eb" : done ? "#16a34a" : "#94a3b8" }}>{s.label}</div>
                <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>{s.sub}</div>
              </div>
            </div>
            {i < STEPS.length - 1 && <div style={{ flex: 1, height: 2, background: done ? "#16a34a" : "#e2e8f0", margin: "0 14px" }} />}
          </div>
        );
      })}
    </div>
  );
}

function SubStepTabs({ active, onSelect, roomsDone, plansDone }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 24, flexWrap: "wrap" }}>
      {[
        { key: "rooms", label: "2a — Room Types", done: roomsDone },
        { key: "plans", label: "2b — Rate Plans", done: plansDone },
      ].map((t) => (
        <button key={t.key} onClick={() => onSelect(t.key)} style={{
          padding: "9px 22px", borderRadius: 8, cursor: "pointer",
          fontFamily: "'Outfit',sans-serif", fontSize: 13, fontWeight: 700,
          border: `2px solid ${active === t.key ? "#2563eb" : "#e2e8f0"}`,
          background: active === t.key ? "#eff6ff" : "#fff",
          color: active === t.key ? "#2563eb" : "#64748b",
          display: "flex", alignItems: "center", gap: 7,
        }}>
          {t.done ? <span style={{ color: "#16a34a", fontWeight: 900 }}>✓</span> : <span style={{ width: 7, height: 7, borderRadius: "50%", background: active === t.key ? "#2563eb" : "#cbd5e1", display: "inline-block" }} />}
          {t.label}
        </button>
      ))}
    </div>
  );
}

function safeArray(v) { return Array.isArray(v) ? v : []; }
function fmtINR(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(x);
}
function toApiDate(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const day = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}
function looksLikeRateInfo(obj) {
  const ri = obj?.rate_info || obj?.rateInfo;
  if (!ri || typeof ri !== "object") return false;
  const nightly = ri.nightly_rates || ri.nightlyRates;
  return Array.isArray(nightly) && nightly.length > 0;
}
function normalizeExtRoomType(rt) {
  return {
    external_id: String(rt?.external_id ?? rt?.externalId ?? rt?.id ?? ""),
    name: String(rt?.name ?? rt?.room_type_name ?? rt?.roomTypeName ?? "Unnamed"),
    max_occupancy: Number(rt?.max_occupancy ?? rt?.maxOccupancy ?? rt?.occupancy ?? 0) || 0,
    saved_mapping: rt?.mapped_room_type_id ?? rt?.our_room_type_id ?? rt?.saved_mapping ?? rt?.mapping ?? "",
  };
}
function normalizeRatePlan(rp) {
  return {
    code: String(rp?.code ?? rp?.rate_plan_code ?? rp?.plan_code ?? ""),
    name: String(rp?.name ?? rp?.rate_plan_name ?? rp?.plan_name ?? "Unnamed Plan"),
    default_channel: rp?.default_channel ?? rp?.defaultChannel ?? rp?.channel_hint ?? "",
    saved_channel: rp?.saved_channel ?? rp?.channel ?? "",
    saved_commission: rp?.saved_commission ?? rp?.commission_pct ?? rp?.commissionPct ?? null,
  };
}

export default function IntegrationPage() {
  const { notify } = useNotification();

  const [mode, setMode] = useState("PMS");
  const [step, setStep] = useState(1);
  const [subStep, setSubStep] = useState("rooms");

  const [loadingInit, setLoadingInit] = useState(true);
  const [statusData, setStatusData] = useState(null);
  const [savingMode, setSavingMode] = useState(false);
  const [testing, setTesting] = useState(false);

  const [connected, setConnected] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const [ourRoomTypes, setOurRoomTypes] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [ratePlans, setRatePlans] = useState([]);
  const [roomMap, setRoomMap] = useState({});
  const [planMap, setPlanMap] = useState({});
  const [roomsDone, setRoomsDone] = useState(false);
  const [plansDone, setPlansDone] = useState(false);
  const [savingRoom, setSavingRoom] = useState(false);
  const [savingPlan, setSavingPlan] = useState(false);
  const [loadingSetupData, setLoadingSetupData] = useState(false);

  const [syncDays, setSyncDays] = useState(30);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncValidation, setSyncValidation] = useState(null);

  const [liveMethod, setLiveMethod] = useState("webhook");
  const [saving, setSaving] = useState(false);
  const [manualOcc, setManualOcc] = useState(65);
  const [unmappedPolicy, setUnmappedPolicy] = useState("queue_and_alert");

  const [setupReceived, setSetupReceived] = useState(false);

  const API_KEY = statusData?.api_key_masked || statusData?.apiKeyMasked || "sk_live_••••••••••";

  const fromDateObj = useMemo(() => new Date(Date.now() - syncDays * 86400000), [syncDays]);
  const toDateObj = useMemo(() => new Date(), []);
  const fromDateApi = toApiDate(fromDateObj);
  const toDateApi = toApiDate(toDateObj);
  const fromDateLabel = fromDateObj.toLocaleDateString("en-IN");
  const toDateLabel = toDateObj.toLocaleDateString("en-IN");

  const roomMappedCount = Object.values(roomMap).filter((v) => v !== "").length;
  const totalRoomTypes = roomTypes.length;
  const allRoomsMapped = totalRoomTypes > 0 && roomMappedCount === totalRoomTypes;

  const planMappedCount = Object.values(planMap).filter((v) => v?.channel).length;
  const totalRatePlans = ratePlans.length;
  const allPlansMapped = totalRatePlans > 0 && planMappedCount === totalRatePlans;
  const canProceedStep2 = roomsDone && plansDone;

  const waitingForVendorSetup = connected && !setupReceived && roomTypes.length === 0 && ratePlans.length === 0;
  const canOpenStep2 = connected && setupReceived && roomTypes.length > 0;

  const inputSt = {
    width: "100%",
    padding: "9px 13px",
    border: "1.5px solid #e2e8f0",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    color: "#1e293b",
    outline: "none",
    background: "#fff",
    fontFamily: "'Outfit',sans-serif",
  };

  const copyText = (text, label) => {
    navigator.clipboard?.writeText(String(text || "")).then(
      () => notify("info", `${label} copied to clipboard.`),
      () => notify("error", `Could not copy ${label.toLowerCase()}.`)
    );
  };

  const resetWizardData = () => {
    setStep(1);
    setSubStep("rooms");
    setConnected(false);
    setSetupReceived(false);
    setRoomTypes([]);
    setRatePlans([]);
    setRoomMap({});
    setPlanMap({});
    setRoomsDone(false);
    setPlansDone(false);
    setSyncResult(null);
    setSyncValidation(null);
  };

  const hydrateFromStatus = (data) => {
    if (!data || typeof data !== "object") return;
    const backendMode = data.mode || data.integration_mode;
    if (backendMode && ["PMS", "INTEGRATION", "MANUAL"].includes(backendMode)) setMode(backendMode);
    if (backendMode === "INTEGRATION") setConnected(!!(data.connection_verified || data.connected || data.api_key_verified));
    if (backendMode === "MANUAL" && Number.isFinite(Number(data.manual_occupancy_pct))) setManualOcc(Number(data.manual_occupancy_pct));
    if (data.live_method && ["webhook", "daily"].includes(String(data.live_method).toLowerCase())) {
      setLiveMethod(String(data.live_method).toLowerCase());
    }
    if (data.unmapped_room_policy) setUnmappedPolicy(data.unmapped_room_policy);

    const s1 = !!(data.setup_received || data.vendor_setup_received || data.setup_called);
    setSetupReceived(s1);

    if (data.mapping_status) {
      setRoomsDone(!!data.mapping_status.rooms_done);
      setPlansDone(!!data.mapping_status.rate_plans_done);
    }
    if (data.current_step && Number.isFinite(Number(data.current_step))) {
      setStep(Math.min(4, Math.max(1, Number(data.current_step))));
    }
  };

  const loadStatus = async () => {
    const res = await api.getIntegrationStatus(companyId);
    const data = res?.data?.data ?? res?.data ?? {};
    setStatusData(data);
    hydrateFromStatus(data);

    const ours = safeArray(data.our_room_types || data.internal_room_types || data.property_room_types || []);
    if (ours.length) {
      setOurRoomTypes(ours.map((r) => ({
        id: r.id ?? r.room_type_id,
        name: r.name ?? r.type_name ?? r.room_type_name,
        base_rate: Number(r.base_rate ?? r.base_rate_single ?? 0) || 0,
      })));
    }
  };

  const loadExternalSetupData = async ({ silent = false } = {}) => {
    if (!silent) setLoadingSetupData(true);
    try {
      const [extRtRes, extRpRes, savedRoomsRes] = await Promise.all([
        api.getExternalRoomTypes(companyId),
        api.getExternalRatePlans(companyId),
        api.getSavedRoomMapping(companyId).catch(() => null),
      ]);

      const rawRooms = safeArray(extRtRes?.data?.data ?? extRtRes?.data ?? []);
      const rawPlans = safeArray(extRpRes?.data?.data ?? extRpRes?.data ?? []);
      const normalizedRooms = rawRooms.map(normalizeExtRoomType).filter((x) => x.external_id);
      const normalizedPlans = rawPlans.map(normalizeRatePlan).filter((x) => x.code);

      const savedRoomPayload = savedRoomsRes?.data?.data ?? savedRoomsRes?.data ?? [];
      const savedRoomList = Array.isArray(savedRoomPayload)
        ? savedRoomPayload
        : Array.isArray(savedRoomPayload?.mappings)
          ? savedRoomPayload.mappings
          : [];
      const savedRoomIndex = savedRoomList.reduce((acc, m) => {
        const k = String(m.external_id ?? m.external_room_type_id ?? "");
        if (!k) return acc;
        acc[k] = String(m.room_type_id ?? m.our_room_type_id ?? m.mapped_room_type_id ?? m.mapping ?? "");
        return acc;
      }, {});

      setRoomTypes(normalizedRooms);
      setRatePlans(normalizedPlans);
      setSetupReceived(normalizedRooms.length > 0 || normalizedPlans.length > 0 || setupReceived);

      setRoomMap((prev) => {
        const next = {};
        normalizedRooms.forEach((rt) => {
          next[rt.external_id] = prev[rt.external_id] ?? savedRoomIndex[rt.external_id] ?? (rt.saved_mapping ? String(rt.saved_mapping) : "");
        });
        return next;
      });

      setPlanMap((prev) => {
        const next = {};
        normalizedPlans.forEach((rp) => {
          const existing = prev[rp.code] || {};
          next[rp.code] = {
            channel: existing.channel || rp.saved_channel || rp.default_channel || "",
            commission_pct:
              existing.commission_pct !== undefined
                ? existing.commission_pct
                : (rp.saved_commission != null ? String(rp.saved_commission) : ""),
          };
        });
        return next;
      });

      const roomsMapped = normalizedRooms.length > 0 && normalizedRooms.every((rt) => {
        const v = savedRoomIndex[rt.external_id] || rt.saved_mapping;
        return String(v || "") !== "";
      });
      setRoomsDone(roomsMapped || roomsDone);

      const plansMapped = normalizedPlans.length > 0 && normalizedPlans.every((rp) => String(rp.saved_channel || "") !== "");
      setPlansDone(plansMapped || plansDone);

      if (!silent) {
        if (normalizedRooms.length || normalizedPlans.length) {
          notify("success", `${normalizedRooms.length} room types + ${normalizedPlans.length} rate plans loaded from vendor setup.`);
        } else {
          notify("info", "Vendor setup not received yet. Ask vendor to call /setup first.");
        }
      }

      return { rooms: normalizedRooms, plans: normalizedPlans };
    } catch (err) {
      if (!silent) notify("error", err?.response?.data?.message || "Could not load setup data. Has vendor called /setup yet?");
      return { rooms: [], plans: [] };
    } finally {
      if (!silent) setLoadingSetupData(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingInit(true);
      try {
        await loadStatus();
        if (mounted) await loadExternalSetupData({ silent: true });
      } catch {
        if (mounted) notify("error", "Failed to load integration status.");
      } finally {
        if (mounted) setLoadingInit(false);
      }
    })();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSaveMode = async (modeKey, extra = {}) => {
    setSavingMode(true);
    try {
      await api.saveIntegrationMode(companyId, modeKey, extra);
      setMode(modeKey);
      setStatusData((s) => ({ ...(s || {}), mode: modeKey, ...extra }));
      notify("success", `${modeKey === "PMS" ? "Native PMS" : modeKey === "INTEGRATION" ? "3rd-party PMS" : "Manual"} mode saved.`);
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to save integration mode.");
    } finally {
      setSavingMode(false);
    }
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      await api.testIntegrationConnection(companyId);
      setConnected(true);
      notify("success", "API key verified — server accepted credentials.");
      await loadStatus().catch(() => {});
    } catch (err) {
      notify("error", err?.response?.data?.message || "Connection failed. Check API key / server config.");
    } finally {
      setTesting(false);
    }
  };

  const handleLoadSetupData = async () => {
    const r = await loadExternalSetupData();
    if (r.rooms.length || r.plans.length) {
      setStep(2);
      setSubStep("rooms");
    }
  };

  const handleSaveRoomMapping = async () => {
    setSavingRoom(true);
    try {
      const mappings = roomTypes.map((rt) => ({
        external_id: rt.external_id,
        room_type_id: roomMap[rt.external_id] === "ignore" ? null : Number(roomMap[rt.external_id]),
        ignore: roomMap[rt.external_id] === "ignore",
      }));
      await api.saveRoomMapping(companyId, mappings);
      setRoomsDone(true);
      notify("success", "Room type mapping saved.");
      setSubStep("plans");
      await loadStatus().catch(() => {});
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to save room mapping.");
    } finally {
      setSavingRoom(false);
    }
  };

  const handleSaveRatePlanMapping = async () => {
    setSavingPlan(true);
    try {
      const mappings = ratePlans.map((rp) => {
        const v = planMap[rp.code] || { channel: "", commission_pct: "" };
        return {
          code: rp.code,
          channel: v.channel,
          commission_pct: v.channel === "OTA"
            ? Number(v.commission_pct === "" ? DEFAULT_COMMISSIONS.OTA : v.commission_pct)
            : 0,
        };
      });
      await api.saveRatePlanMapping(companyId, mappings);
      setPlansDone(true);
      notify("success", "Rate plan mapping saved — channels and commissions configured.");
      await loadStatus().catch(() => {});
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to save rate plan mapping.");
    } finally {
      setSavingPlan(false);
    }
  };

  const validateSyncResponse = (payload) => {
    const data = payload?.data ?? payload ?? {};
    const summary = data.summary || data.result || data;
    const sampleBookings = safeArray(data.sample_bookings || data.sampleBookings || summary.sample_bookings || summary.sampleBookings || []);
    const imported = Number(summary.inserted ?? summary.imported ?? data.inserted ?? 0) || 0;
    const skipped = Number(summary.skipped ?? data.skipped ?? 0) || 0;
    const total = Number(summary.total ?? data.total ?? imported + skipped) || (imported + skipped);
    const withRateInfo = Number(summary.with_rate_info ?? summary.rate_info_count ?? data.with_rate_info ?? 0);
    const withoutRateInfo = Number(summary.without_rate_info ?? data.without_rate_info ?? 0);
    const missingRatePlan = Number(summary.missing_rate_plan_mapping ?? data.missing_rate_plan_mapping ?? 0);
    const missingRoomMap = Number(summary.unmapped_room_types ?? data.unmapped_room_types ?? 0);
    const grossRevenue = Number(summary.gross_room_revenue ?? data.gross_room_revenue ?? 0) || 0;
    const taxAmount = Number(summary.tax_amount ?? summary.total_tax ?? data.tax_amount ?? 0) || 0;

    let computedWithRate = withRateInfo;
    if (!computedWithRate && sampleBookings.length) computedWithRate = sampleBookings.filter(looksLikeRateInfo).length;
    let computedWithoutRate = withoutRateInfo;
    if (!computedWithoutRate && total) computedWithoutRate = Math.max(0, total - computedWithRate);

    return {
      inserted: imported,
      skipped,
      total,
      withRateInfo: computedWithRate,
      withoutRateInfo: computedWithoutRate,
      missingRatePlan,
      missingRoomMap,
      grossRevenue,
      taxAmount,
      sampleBookings,
      raw: data,
    };
  };

  const handleBulkSync = async () => {
    setSyncing(true);
    try {
      const res = await api.triggerBulkSync(companyId, fromDateApi, toDateApi);
      const parsed = validateSyncResponse(res?.data ?? res);
      setSyncResult(parsed);
      setSyncValidation(parsed);
      notify(
        parsed.withoutRateInfo > 0
          ? "error"
          : "success",
        parsed.withoutRateInfo > 0
          ? `Sync completed, but ${parsed.withoutRateInfo} booking(s) missing rate_info. Ask vendor to resend with nightly rates + tax.`
          : `Sync complete — ${parsed.inserted} bookings imported with rate_info.`
      );
      await loadStatus().catch(() => {});
    } catch (err) {
      notify("error", err?.response?.data?.message || "Bulk sync failed.");
    } finally {
      setSyncing(false);
    }
  };

  const handleGoLive = async () => {
    setSaving(true);
    try {
      await api.saveIntegrationMode(companyId, "INTEGRATION", {
        live_method: liveMethod,
        unmapped_room_policy: unmappedPolicy,
        go_live: true,
      });
      notify("success", "Integration live — AI pricing now uses booking + rate data from vendor feeds.");
      await loadStatus().catch(() => {});
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to save go-live settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveManual = async () => {
    setSaving(true);
    try {
      await api.saveIntegrationMode(companyId, "MANUAL", { manual_occupancy_pct: Number(manualOcc) });
      notify("success", `Manual occupancy set to ${manualOcc}%.`);
      await loadStatus().catch(() => {});
    } catch (err) {
      notify("error", err?.response?.data?.message || "Failed to save manual settings.");
    } finally {
      setSaving(false);
    }
  };

  const setupPayloadPreview = `POST ${BASE}/setup\nAuthorization: Bearer YOUR_API_KEY\n\n{\n  "companyid": ${companyId},\n  "room_types": [\n    { "external_id": "RT001", "name": "Superior Room", "max_occupancy": 2 },\n    { "external_id": "RT002", "name": "Deluxe King", "max_occupancy": 2 }\n  ],\n  "rate_plans": [\n    { "code": "BAR", "name": "Best Available Rate", "default_channel": "PMS" },\n    { "code": "MMT_STD", "name": "MakeMyTrip Standard", "default_channel": "OTA" }\n  ]\n}\n\n// Vendor MUST call /setup first. Step 2 stays locked until room list arrives.`;

  const bulkPayloadPreview = `POST ${BASE}/bulk-sync\nAuthorization: Bearer YOUR_API_KEY\n\n{\n  "companyid": ${companyId},\n  "from_date": "${fromDateApi}",\n  "to_date": "${toDateApi}",\n  "bookings": [\n    {\n      "external_booking_id": "BK-001",\n      "external_room_type_id": "RT002",\n      "check_in_date": "2026-03-01",\n      "check_out_date": "2026-03-03",\n      "status": "confirmed",\n      "num_rooms": 1,\n      "rate_info": {\n        "rate_plan_code": "MMT_STD",\n        "currency": "INR",\n        "nightly_rates": [\n          { "date": "2026-03-01", "room_rate": 4800, "tax": 864, "total": 5664 },\n          { "date": "2026-03-02", "room_rate": 4800, "tax": 864, "total": 5664 }\n        ],\n        "total_room_revenue": 9600,\n        "total_tax": 1728\n      }\n    }\n  ]\n}\n\n// rate_info is REQUIRED for realised-rate benchmarking.`;

  const webhookCreatedPreview = `POST ${BASE}/webhook\nAuthorization: Bearer YOUR_API_KEY\n\n{\n  "event": "booking_created",\n  "companyid": ${companyId},\n  "timestamp": "2026-02-24T14:30:00Z",\n  "data": {\n    "external_booking_id": "BK-2890",\n    "external_room_type_id": "RT002",\n    "status": "confirmed",\n    "check_in_date": "2026-03-01",\n    "check_out_date": "2026-03-03",\n    "num_rooms": 1,\n    "rate_info": { ...same structure as bulk-sync... }\n  }\n}`;
  const webhookModifiedPreview = `POST ${BASE}/webhook\nAuthorization: Bearer YOUR_API_KEY\n\n{\n  "event": "booking_modified",\n  "companyid": ${companyId},\n  "timestamp": "2026-02-24T15:00:00Z",\n  "data": {\n    "external_booking_id": "BK-2890",\n    "previous": { "check_out_date": "2026-03-02" },\n    "current": {\n      "check_out_date": "2026-03-03",\n      "num_rooms": 1,\n      "rate_info": { ...updated nightly rates... }\n    }\n  }\n}`;
  const webhookCancelledPreview = `POST ${BASE}/webhook\nAuthorization: Bearer YOUR_API_KEY\n\n{\n  "event": "booking_cancelled",\n  "companyid": ${companyId},\n  "timestamp": "2026-02-24T16:10:00Z",\n  "data": {\n    "external_booking_id": "BK-2890",\n    "external_room_type_id": "RT002",\n    "status": "cancelled",\n    "cancelled_at": "2026-02-24T16:08:00Z"\n  }\n}`;

  const MODES = [
    { key: "PMS", icon: "🏨", title: "Your PMS (Native)", badge: "Recommended", bc: "#16a34a", desc: "AI reads live from your database. No vendor /setup, no mapping wizard." },
    { key: "INTEGRATION", icon: "🔗", title: "3rd Party PMS", badge: "Advanced", bc: "#2563eb", desc: "Vendor sends setup + history + webhook. Includes room/rate-plan mapping." },
    { key: "MANUAL", icon: "✏️", title: "Manual / No PMS", badge: "Quick Start", bc: "#d97706", desc: "Use fixed occupancy %. Fastest start, least accurate." },
  ];

  const selectModeCard = (newMode) => {
    setMode(newMode);
    if (newMode === "INTEGRATION") resetWizardData();
    if (newMode === "MANUAL") setStep(1);
    if (newMode === "PMS") setStep(1);
  };

  if (loadingInit) {
    return <div style={{ padding: "28px 32px", color: "#64748b", fontWeight: 500 }}>Loading integration setup…</div>;
  }

  return (
    <div style={{ padding: "28px 32px" }}>
      <PageHeader title="Integration Setup" subtitle="Configure how AI pricing receives occupancy, channel attribution and realised rates" />

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16, marginBottom: 28 }}>
        {MODES.map((m) => {
          const active = mode === m.key;
          return (
            <div key={m.key} onClick={() => selectModeCard(m.key)} style={{
              background: "#fff", borderRadius: 14, padding: 22,
              border: `2px solid ${active ? "#2563eb" : "#e2e8f0"}`,
              cursor: "pointer",
              boxShadow: active ? "0 4px 20px rgba(37,99,235,0.14)" : "0 1px 4px rgba(0,0,0,0.04)",
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
                <span style={{ fontSize: 28 }}>{m.icon}</span>
                <span style={{ fontSize: 10, fontWeight: 800, color: m.bc, background: `${m.bc}15`, border: `1.5px solid ${m.bc}40`, borderRadius: 20, padding: "3px 10px" }}>{m.badge}</span>
              </div>
              <div style={{ fontSize: 14, fontWeight: 800, color: "#0f172a", marginBottom: 7 }}>{m.title}</div>
              <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.7, fontWeight: 500 }}>{m.desc}</div>
              {active && <div style={{ marginTop: 10, fontSize: 11, color: "#2563eb", fontWeight: 800 }}>✓ Selected</div>}
            </div>
          );
        })}
      </div>

      {mode === "PMS" && (
        <Card style={{ padding: "28px 32px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, marginBottom: 14, flexWrap: "wrap" }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#16a34a" }} />
                <div style={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>Native PMS — Direct DB Read</div>
              </div>
              <div style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>
                Use your own PMS/database tables directly. No vendor /setup, no room mapping, no webhook onboarding.
              </div>
            </div>
            <button className="dp-btn dp-btn-primary" onClick={() => handleSaveMode("PMS", { source: "NATIVE_PMS" })} disabled={savingMode}>
              {savingMode ? <><span className="dp-spin">⟳</span> Saving...</> : "💾 Save Native PMS Mode"}
            </button>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 10, marginBottom: 20 }}>
            {[
              { label: "Database", value: statusData?.native_status?.database || "Connected", ok: true },
              { label: "Occupancy Source", value: statusData?.native_status?.occupancy_table || "ns_room_bookings", ok: true },
              { label: "Rate Source", value: statusData?.native_status?.rate_table || "ns_room_type_rates", ok: true },
              { label: "Rate Fallback", value: "ns_room_types.base_rate", ok: true },
              { label: "Channel-aware Pricing", value: "PMS / BE / OTA", ok: true },
              { label: "Commission Handling", value: "From Settings", ok: true },
            ].map((item) => <StatusRow key={item.label} label={item.label} value={item.value} ok={item.ok} />)}
          </div>

          <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, padding: "14px 18px" }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#15803d", marginBottom: 6 }}>How pricing is resolved</div>
            <div style={{ fontSize: 13, color: "#166534", lineHeight: 1.8, fontWeight: 500 }}>
              AI reads occupancy and realised rates from your native tables. It compares channel-specific rates (PMS/BE/OTA) and applies OTA commission logic using your saved settings. No external mapping is required.
            </div>
          </div>
        </Card>
      )}

      {mode === "INTEGRATION" && (
        <div>
          <StepBar current={step} />

          {step === 1 && (
            <Card style={{ padding: "28px 32px" }}>
              <SectionTitle title="Step 1 — Connect" sub="Vendor must call /setup first to push room types + rate plans. Step 2 stays locked until setup data is received." />

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(340px,1fr))", gap: 20, marginBottom: 22 }}>
                <div>
                  <label className="dp-label">API Key</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    <input type={showKey ? "text" : "password"} value={API_KEY} readOnly style={{ ...inputSt, fontFamily: "'DM Mono',monospace", fontWeight: 600, background: "#f8fafc", flex: 1 }} />
                    <button className="dp-btn dp-btn-ghost dp-btn-sm" onClick={() => setShowKey(!showKey)}>{showKey ? "Hide" : "Show"}</button>
                    <button className="dp-btn dp-btn-outline dp-btn-sm" onClick={() => copyText(API_KEY, "API key")}>Copy</button>
                  </div>

                  <label className="dp-label">Vendor setup endpoint</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    <input type="text" value={`${BASE}/setup`} readOnly style={{ ...inputSt, fontFamily: "'DM Mono',monospace", fontSize: 11, background: "#f8fafc", flex: 1 }} />
                    <button className="dp-btn dp-btn-outline dp-btn-sm" onClick={() => copyText(`${BASE}/setup`, "Setup endpoint")}>Copy</button>
                  </div>

                  <label className="dp-label">Bulk history sync endpoint</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    <input type="text" value={`${BASE}/bulk-sync`} readOnly style={{ ...inputSt, fontFamily: "'DM Mono',monospace", fontSize: 11, background: "#f8fafc", flex: 1 }} />
                    <button className="dp-btn dp-btn-outline dp-btn-sm" onClick={() => copyText(`${BASE}/bulk-sync`, "Bulk sync endpoint")}>Copy</button>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                    <button className="dp-btn dp-btn-primary" onClick={handleTestConnection} disabled={testing}>
                      {testing ? <><span className="dp-spin">⟳</span> Testing...</> : "🔌 Test API Key"}
                    </button>
                    {connected && <span style={{ fontSize: 13, color: "#15803d", fontWeight: 700 }}>✓ API key verified</span>}
                  </div>
                </div>

                <div style={{ background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 12, padding: "16px 18px" }}>
                  <div style={{ fontSize: 13, fontWeight: 800, color: "#1d4ed8", marginBottom: 10 }}>📋 Share this with vendor tech team</div>
                  {[
                    `companyid: ${companyId}`,
                    "Order: 1) /setup  2) /bulk-sync  3) /webhook (or daily bulk-sync)",
                    "/setup must include room_types[] + rate_plans[]",
                    "Room types must include external_id + name + max_occupancy",
                    "Bulk sync and webhook payloads must include rate_info (nightly rates + tax)",
                    "Send rate_plan_code so channel + commission mapping can resolve net rate",
                  ].map((item) => (
                    <div key={item} style={{ fontSize: 12, color: "#1e40af", marginBottom: 6, display: "flex", gap: 6, fontWeight: 500 }}>
                      <span style={{ color: "#3b82f6", flexShrink: 0 }}>●</span>{item}
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
                <div style={{ background: connected ? "#f0fdf4" : "#f8fafc", border: `1px solid ${connected ? "#86efac" : "#e2e8f0"}`, borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 800, marginBottom: 4 }}>Connection</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: connected ? "#15803d" : "#94a3b8" }}>{connected ? "Verified" : "Not verified"}</div>
                </div>
                <div style={{ background: setupReceived ? "#f0fdf4" : "#fffbeb", border: `1px solid ${setupReceived ? "#86efac" : "#fde68a"}`, borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.6, fontWeight: 800, marginBottom: 4 }}>/setup status</div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: setupReceived ? "#15803d" : "#b45309" }}>{setupReceived ? "Received — room list available" : "Waiting for vendor to call /setup"}</div>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <CodeBlock label="/setup payload — vendor sends room types + rate plans" code={setupPayloadPreview} />
              </div>

              {waitingForVendorSetup && (
                <div style={{ padding: "12px 15px", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 8, marginBottom: 16, fontSize: 12, color: "#92400e", fontWeight: 500 }}>
                  ⏳ Waiting state: vendor has not pushed room types yet. Step 2 remains locked until /setup data is received.
                </div>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <button className="dp-btn dp-btn-outline" onClick={() => handleSaveMode("INTEGRATION", { current_step: 1 })} disabled={savingMode}>
                  {savingMode ? <><span className="dp-spin">⟳</span> Saving...</> : "💾 Save Integration Mode"}
                </button>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <button className="dp-btn dp-btn-outline" onClick={handleLoadSetupData} disabled={!connected || loadingSetupData}>
                    {loadingSetupData ? <><span className="dp-spin">⟳</span> Checking /setup...</> : "Check / Refresh Setup Data"}
                  </button>
                  <button className="dp-btn dp-btn-primary" onClick={() => setStep(2)} disabled={!canOpenStep2}>
                    Next: Map Rooms →
                  </button>
                </div>
              </div>
            </Card>
          )}

          {step === 2 && (
            <Card style={{ padding: "28px 32px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 800, color: "#0f172a", marginBottom: 4 }}>Step 2 — Configure Mappings</div>
                  <div style={{ fontSize: 13, color: "#64748b", fontWeight: 500 }}>Use vendor-pushed room types and rate plans from database (via /setup). No hardcoded lists.</div>
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: roomsDone ? "#15803d" : "#94a3b8" }}>{roomsDone ? "✓" : "○"} Rooms</span>
                  <span style={{ color: "#e2e8f0" }}>|</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: plansDone ? "#15803d" : "#94a3b8" }}>{plansDone ? "✓" : "○"} Rate Plans</span>
                </div>
              </div>

              {!setupReceived || (roomTypes.length === 0 && ratePlans.length === 0) ? (
                <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 12, padding: "28px 22px", textAlign: "center" }}>
                  <div style={{ fontSize: 26, marginBottom: 8 }}>⏳</div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a", marginBottom: 6 }}>Waiting for vendor room list</div>
                  <div style={{ fontSize: 12, color: "#64748b", marginBottom: 14 }}>The vendor must call <code>{`${BASE}/setup`}</code> first. This page will populate from <strong>ai_external_room_types</strong> and external rate plans.</div>
                  <button className="dp-btn dp-btn-outline" onClick={handleLoadSetupData} disabled={loadingSetupData}>
                    {loadingSetupData ? <><span className="dp-spin">⟳</span> Refreshing...</> : "Refresh Setup Data"}
                  </button>
                </div>
              ) : (
                <>
                  <SubStepTabs active={subStep} onSelect={setSubStep} roomsDone={roomsDone} plansDone={plansDone} />

                  {subStep === "rooms" && (
                    <div>
                      <div style={{ fontSize: 13, color: "#475569", marginBottom: 16, lineHeight: 1.7, fontWeight: 500 }}>
                        Map by <strong>external_id</strong> (not just name). Each row shows vendor room type + capacity so revenue managers can map confidently.
                      </div>

                      {ourRoomTypes.length > 0 && (
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 12, marginBottom: 18 }}>
                          {ourRoomTypes.map((rt, i) => (
                            <div key={rt.id} style={{ flex: 1, padding: "12px 16px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", borderTop: `3px solid ${["#16a34a", "#2563eb", "#8b5cf6", "#f59e0b"][i % 4]}` }}>
                              <div style={{ fontSize: 10, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, marginBottom: 3 }}>ID: {rt.id}</div>
                              <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{rt.name}</div>
                              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#64748b", marginTop: 2 }}>{fmtINR(rt.base_rate)}/night</div>
                            </div>
                          ))}
                        </div>
                      )}

                      <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "100px 1.1fr 80px 1fr 160px", padding: "9px 18px", background: "#f8fafc", borderBottom: "2px solid #e2e8f0", gap: 12 }}>
                          {["External ID", "Vendor Room Type", "Max Pax", "Maps To (Our Type)", "Status"].map((h) => (
                            <span key={h} style={{ fontSize: 10, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.8 }}>{h}</span>
                          ))}
                        </div>
                        {roomTypes.map((rt, i) => {
                          const val = roomMap[rt.external_id] || "";
                          const ourType = ourRoomTypes.find((r) => String(r.id) === String(val));
                          const isIgn = val === "ignore";
                          return (
                            <div key={rt.external_id} style={{ display: "grid", gridTemplateColumns: "100px 1.1fr 80px 1fr 160px", padding: "12px 18px", gap: 12, alignItems: "center", background: i % 2 === 0 ? "#fff" : "#fafafa", borderBottom: i < roomTypes.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, color: "#475569", fontWeight: 700, background: "#f1f5f9", padding: "3px 8px", borderRadius: 5 }}>{rt.external_id}</span>
                              <div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{rt.name}</div>
                                <div style={{ fontSize: 11, color: "#94a3b8" }}>Map persists by external_id</div>
                              </div>
                              <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>≤ {rt.max_occupancy || "—"}</span>
                              <select
                                value={val}
                                onChange={(e) => setRoomMap((m) => ({ ...m, [rt.external_id]: e.target.value }))}
                                style={{ width: "100%", padding: "7px 10px", border: `1.5px solid ${val ? "#bfdbfe" : "#e2e8f0"}`, borderRadius: 7, fontSize: 12, fontWeight: 600, color: val ? "#1e293b" : "#94a3b8", background: val ? "#eff6ff" : "#f8fafc", outline: "none", cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}
                              >
                                <option value="">— Select —</option>
                                {ourRoomTypes.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                                <option value="ignore">🚫 Ignore</option>
                              </select>
                              <span style={{ fontSize: 11, fontWeight: 700, color: !val ? "#94a3b8" : isIgn ? "#64748b" : "#15803d" }}>
                                {!val ? "Not mapped" : isIgn ? "Excluded" : `Mapped → ${ourType?.name || val}`}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                        <div style={{ flex: 1, background: "#e2e8f0", borderRadius: 4, height: 6 }}>
                          <div style={{ width: `${totalRoomTypes ? (roomMappedCount / totalRoomTypes) * 100 : 0}%`, height: "100%", background: "#2563eb", borderRadius: 4 }} />
                        </div>
                        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{roomMappedCount} / {totalRoomTypes} mapped</span>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                        <button className="dp-btn dp-btn-ghost" onClick={() => setStep(1)}>← Back to Step 1</button>
                        <button className="dp-btn dp-btn-primary" onClick={handleSaveRoomMapping} disabled={!allRoomsMapped || savingRoom}>
                          {savingRoom ? <><span className="dp-spin">⟳</span> Saving...</> : "Save Room Mapping →"}
                        </button>
                      </div>
                    </div>
                  )}

                  {subStep === "plans" && (
                    <div>
                      <div style={{ fontSize: 13, color: "#475569", marginBottom: 16, lineHeight: 1.7, fontWeight: 500 }}>
                        Map vendor rate plans to channel (PMS/BE/OTA). This is required to calculate realised net rate per night from <code>rate_info</code>.
                      </div>

                      <div style={{ border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden", marginBottom: 16 }}>
                        <div style={{ display: "grid", gridTemplateColumns: "110px 1fr 100px 150px 150px 150px", padding: "9px 18px", background: "#f8fafc", borderBottom: "2px solid #e2e8f0", gap: 12 }}>
                          {["Plan Code", "Rate Plan Name", "Vendor Hint", "Your Channel", "Commission %", "Net Preview"].map((h) => (
                            <span key={h} style={{ fontSize: 10, fontWeight: 800, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.7 }}>{h}</span>
                          ))}
                        </div>
                        {ratePlans.map((rp, i) => {
                          const v = planMap[rp.code] || { channel: "", commission_pct: "" };
                          const ch = v.channel;
                          const commPct = v.commission_pct !== "" ? Number(v.commission_pct) : (ch ? DEFAULT_COMMISSIONS[ch] : null);
                          const netPct = commPct != null ? 100 - commPct : null;
                          const cc = CH_CFG[ch];
                          return (
                            <div key={rp.code} style={{ display: "grid", gridTemplateColumns: "110px 1fr 100px 150px 150px 150px", padding: "13px 18px", gap: 12, alignItems: "center", background: i % 2 === 0 ? "#fff" : "#fafafa", borderBottom: i < ratePlans.length - 1 ? "1px solid #f1f5f9" : "none" }}>
                              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, fontWeight: 700, color: "#475569", background: "#f1f5f9", padding: "3px 8px", borderRadius: 5 }}>{rp.code}</span>
                              <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{rp.name}</div>
                              {rp.default_channel ? <ChannelBadge ch={rp.default_channel} /> : <span style={{ fontSize: 11, color: "#94a3b8" }}>—</span>}
                              <select
                                value={ch}
                                onChange={(e) => setPlanMap((m) => ({ ...m, [rp.code]: { ...v, channel: e.target.value, commission_pct: "" } }))}
                                style={{ width: "100%", padding: "7px 10px", border: `1.5px solid ${cc ? cc.border : "#e2e8f0"}`, borderRadius: 7, fontSize: 12, fontWeight: 700, color: cc ? cc.color : "#94a3b8", background: cc ? cc.bg : "#f8fafc", outline: "none", cursor: "pointer", fontFamily: "'Outfit',sans-serif" }}>
                                <option value="">— Select —</option>
                                <option value="PMS">PMS</option>
                                <option value="BE">Booking Engine</option>
                                <option value="OTA">OTA</option>
                              </select>
                              {ch === "OTA" ? (
                                <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                  <input type="number" min={0} max={50} value={v.commission_pct} placeholder={`${DEFAULT_COMMISSIONS.OTA}`}
                                    onChange={(e) => setPlanMap((m) => ({ ...m, [rp.code]: { ...v, commission_pct: e.target.value } }))}
                                    style={{ width: 60, padding: "7px 8px", border: "1.5px solid #f9a8d4", borderRadius: 7, fontSize: 12, fontWeight: 700, color: "#be185d", background: "#fdf2f8", outline: "none", fontFamily: "'DM Mono',monospace" }} />
                                  <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>%</span>
                                </div>
                              ) : (
                                <span style={{ fontSize: 12, color: ch ? "#15803d" : "#94a3b8", fontWeight: 600 }}>{ch ? "0%" : "—"}</span>
                              )}
                              <div>
                                {!ch && <span style={{ fontSize: 11, color: "#94a3b8" }}>Select channel</span>}
                                {ch && ch !== "OTA" && <span style={{ fontSize: 11, color: "#15803d", fontWeight: 700 }}>100% net to hotel</span>}
                                {ch === "OTA" && netPct != null && (
                                  <div>
                                    <div style={{ fontSize: 12, fontWeight: 800, color: "#be185d" }}>{netPct.toFixed(0)}% net</div>
                                    <div style={{ fontSize: 10, color: "#94a3b8" }}>₹5,000 → ₹{Math.round(5000 * netPct / 100).toLocaleString("en-IN")}</div>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                        <div style={{ flex: 1, background: "#e2e8f0", borderRadius: 4, height: 6 }}>
                          <div style={{ width: `${totalRatePlans ? (planMappedCount / totalRatePlans) * 100 : 0}%`, height: "100%", background: "#be185d", borderRadius: 4 }} />
                        </div>
                        <span style={{ fontSize: 12, color: "#64748b", fontWeight: 600 }}>{planMappedCount} / {totalRatePlans} mapped</span>
                      </div>

                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                        <button className="dp-btn dp-btn-ghost" onClick={() => setSubStep("rooms")}>← Back to 2a</button>
                        <button className="dp-btn dp-btn-primary" onClick={handleSaveRatePlanMapping} disabled={!allPlansMapped || savingPlan}>
                          {savingPlan ? <><span className="dp-spin">⟳</span> Saving...</> : "Save Rate Plan Mapping"}
                        </button>
                      </div>

                      {canProceedStep2 && (
                        <div style={{ marginTop: 14, padding: "12px 16px", background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 10, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 13, color: "#15803d", fontWeight: 700 }}>✓ Both mappings complete — ready for history sync</span>
                          <button className="dp-btn dp-btn-success" onClick={() => setStep(3)}>Proceed to Step 3 →</button>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </Card>
          )}

          {step === 3 && (
            <Card style={{ padding: "28px 32px" }}>
              <SectionTitle title="Step 3 — Initial Booking Sync" sub="Push historical bookings with rate_info (nightly rates + tax). AI benchmarks realised net rate per night — not occupancy only." />

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 14, marginBottom: 20 }}>
                {[
                  { icon: "💵", title: "rate_info required", desc: "Include nightly room_rate + tax + rate_plan_code for every booking." },
                  { icon: "📉", title: "Realised-rate benchmarking", desc: "AI learns what you actually realised net of OTA commission." },
                  { icon: "🛡", title: "Duplicate-safe", desc: "Duplicate external_booking_id entries are skipped by backend." },
                ].map((item) => (
                  <div key={item.title} style={{ background: "#f8fafc", borderRadius: 12, padding: "16px 18px", border: "1px solid #e2e8f0" }}>
                    <div style={{ fontSize: 20, marginBottom: 8 }}>{item.icon}</div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 5 }}>{item.title}</div>
                    <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.7, fontWeight: 500 }}>{item.desc}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", alignItems: "center", gap: 20, marginBottom: 20, padding: "13px 18px", background: "#f8fafc", borderRadius: 10, border: "1px solid #e2e8f0", flexWrap: "wrap" }}>
                <div>
                  <label className="dp-label" style={{ marginBottom: 8 }}>Sync window</label>
                  <select value={syncDays} onChange={(e) => setSyncDays(Number(e.target.value))}
                    style={{ padding: "8px 14px", border: "1.5px solid #e2e8f0", borderRadius: 8, fontSize: 13, fontWeight: 600, color: "#1e293b", background: "#fff", outline: "none", fontFamily: "'Outfit',sans-serif" }}>
                    <option value={14}>Last 14 days</option>
                    <option value={30}>Last 30 days</option>
                    <option value={60}>Last 60 days</option>
                    <option value={90}>Last 90 days</option>
                  </select>
                </div>
                <div style={{ marginLeft: "auto", textAlign: "right" }}>
                  <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>Date range</div>
                  <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 14, fontWeight: 800, color: "#0f172a" }}>{fromDateLabel} → {toDateLabel}</div>
                </div>
              </div>

              <div style={{ marginBottom: 20 }}>
                <CodeBlock label="Bulk sync payload — includes rate_info (required)" code={bulkPayloadPreview} />
              </div>

              {!syncResult ? (
                <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20, flexWrap: "wrap" }}>
                  <button className="dp-btn dp-btn-success" onClick={handleBulkSync} disabled={syncing}>
                    {syncing ? <><span className="dp-spin">⟳</span> Triggering Sync...</> : "▶ Trigger Bulk Sync"}
                  </button>
                  <span style={{ fontSize: 12, color: "#94a3b8" }}>or wait for vendor to call /bulk-sync directly</span>
                </div>
              ) : (
                <>
                  <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 12, padding: "18px 22px", marginBottom: 14 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#15803d", marginBottom: 12 }}>✓ Sync Result Summary</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 16 }}>
                      {[
                        { label: "Imported", value: syncResult.inserted },
                        { label: "Skipped", value: syncResult.skipped },
                        { label: "With rate_info", value: syncResult.withRateInfo },
                        { label: "Missing rate_info", value: syncResult.withoutRateInfo },
                        { label: "Room Revenue", value: syncResult.grossRevenue ? fmtINR(syncResult.grossRevenue) : "—" },
                        { label: "Tax", value: syncResult.taxAmount ? fmtINR(syncResult.taxAmount) : "—" },
                      ].map((item) => (
                        <div key={item.label}>
                          <div style={{ fontSize: 10, color: "#16a34a", textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 800, marginBottom: 4 }}>{item.label}</div>
                          <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 18, fontWeight: 800, color: "#14532d" }}>{item.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 }}>
                    <div style={{ background: syncResult.withoutRateInfo > 0 ? "#fef2f2" : "#f0fdf4", border: `1px solid ${syncResult.withoutRateInfo > 0 ? "#fca5a5" : "#86efac"}`, borderRadius: 10, padding: "12px 14px" }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: syncResult.withoutRateInfo > 0 ? "#b91c1c" : "#15803d", marginBottom: 5 }}>rate_info validation</div>
                      <div style={{ fontSize: 12, color: syncResult.withoutRateInfo > 0 ? "#991b1b" : "#166534", lineHeight: 1.6 }}>
                        {syncResult.withoutRateInfo > 0
                          ? `${syncResult.withoutRateInfo} booking(s) missing rate_info. Vendor may be sending occupancy-only payloads. Ask them to include nightly rates + tax breakdown + rate_plan_code.`
                          : "All synced bookings include rate_info. Realised-rate benchmarking is ready."}
                      </div>
                    </div>
                    <div style={{ background: (syncResult.missingRatePlan || syncResult.missingRoomMap) ? "#fffbeb" : "#f8fafc", border: `1px solid ${(syncResult.missingRatePlan || syncResult.missingRoomMap) ? "#fde68a" : "#e2e8f0"}`, borderRadius: 10, padding: "12px 14px" }}>
                      <div style={{ fontSize: 12, fontWeight: 800, color: (syncResult.missingRatePlan || syncResult.missingRoomMap) ? "#b45309" : "#475569", marginBottom: 5 }}>Mapping validation</div>
                      <div style={{ fontSize: 12, color: (syncResult.missingRatePlan || syncResult.missingRoomMap) ? "#92400e" : "#64748b", lineHeight: 1.6 }}>
                        Unmapped room types: {syncResult.missingRoomMap || 0} · Missing rate-plan mappings: {syncResult.missingRatePlan || 0}
                      </div>
                    </div>
                  </div>
                </>
              )}

              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <button className="dp-btn dp-btn-ghost" onClick={() => setStep(2)}>← Back</button>
                <button className="dp-btn dp-btn-primary" onClick={() => setStep(4)} disabled={!syncResult}>
                  Next: Go Live →
                </button>
              </div>
            </Card>
          )}

          {step === 4 && (
            <Card style={{ padding: "28px 32px" }}>
              <SectionTitle title="Step 4 — Go Live" sub="Configure ongoing booking ingestion and unmapped-room fallback policy. Different event types have different payloads." />

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
                {[
                  { key: "webhook", icon: "⚡", title: "Webhook — Real-time", badge: "Recommended", bc: "#6d28d9", desc: "PMS fires event payloads immediately for create / modify / cancel." },
                  { key: "daily", icon: "📅", title: "Daily Push", badge: "Simpler", bc: "#0369a1", desc: "Vendor sends daily bulk-sync snapshot. Easier but delayed." },
                ].map((opt) => (
                  <div key={opt.key} onClick={() => setLiveMethod(opt.key)} style={{ background: "#fff", borderRadius: 12, padding: "18px 20px", border: `2px solid ${liveMethod === opt.key ? "#2563eb" : "#e2e8f0"}`, cursor: "pointer", boxShadow: liveMethod === opt.key ? "0 4px 16px rgba(37,99,235,0.12)" : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                      <span style={{ fontSize: 22 }}>{opt.icon}</span>
                      <span style={{ fontSize: 10, fontWeight: 800, color: opt.bc, background: `${opt.bc}15`, border: `1.5px solid ${opt.bc}40`, borderRadius: 20, padding: "3px 10px" }}>{opt.badge}</span>
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a", marginBottom: 5 }}>{opt.title}</div>
                    <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.6, fontWeight: 500 }}>{opt.desc}</div>
                    {liveMethod === opt.key && <div style={{ marginTop: 8, fontSize: 11, color: "#2563eb", fontWeight: 800 }}>✓ Selected</div>}
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1.1fr 0.9fr", gap: 16, marginBottom: 18 }}>
                <div>
                  <label className="dp-label">{liveMethod === "webhook" ? "Webhook Endpoint" : "Daily Push Endpoint"}</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                    <input type="text" value={liveMethod === "webhook" ? `${BASE}/webhook` : `${BASE}/bulk-sync`} readOnly style={{ ...inputSt, fontFamily: "'DM Mono',monospace", fontSize: 11, background: "#f8fafc", flex: 1 }} />
                    <button className="dp-btn dp-btn-outline" onClick={() => copyText(liveMethod === "webhook" ? `${BASE}/webhook` : `${BASE}/bulk-sync`, "Endpoint")}>Copy</button>
                  </div>

                  <label className="dp-label">Unmapped room fallback policy</label>
                  <select value={unmappedPolicy} onChange={(e) => setUnmappedPolicy(e.target.value)} className="dp-select" style={{ ...inputSt }}>
                    <option value="queue_and_alert">Queue + alert manager (recommended)</option>
                    <option value="skip_event">Skip event + alert</option>
                    <option value="map_to_default">Map to default room type (risky)</option>
                  </select>
                  <div style={{ marginTop: 8, fontSize: 12, color: "#64748b" }}>
                    Defines what happens when vendor sends an external room type not yet mapped in Step 2.
                  </div>
                </div>

                <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 12, padding: "14px 16px" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a", marginBottom: 10 }}>Event Types (different payload semantics)</div>
                  {[
                    ["booking_created", "New booking + full rate_info"],
                    ["booking_modified", "Patch current booking; may include updated rate_info"],
                    ["booking_cancelled", "Release occupancy; rate_info optional"],
                  ].map(([evt, desc]) => (
                    <div key={evt} style={{ marginBottom: 8, display: "flex", gap: 8, alignItems: "flex-start" }}>
                      <code style={{ background: "#eef2ff", color: "#4338ca", borderRadius: 5, padding: "2px 6px", fontSize: 11 }}>{evt}</code>
                      <span style={{ fontSize: 12, color: "#64748b" }}>{desc}</span>
                    </div>
                  ))}
                </div>
              </div>

              {liveMethod === "webhook" && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 12, marginBottom: 18 }}>
                  <CodeBlock label="Webhook payload — booking_created (includes rate_info)" code={webhookCreatedPreview} />
                  <CodeBlock label="Webhook payload — booking_modified (delta + updated rate_info)" code={webhookModifiedPreview} />
                  <CodeBlock label="Webhook payload — booking_cancelled" code={webhookCancelledPreview} />
                </div>
              )}

              <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 12, padding: "18px 22px", marginBottom: 20 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#15803d", marginBottom: 12 }}>Integration Checklist</div>
                {[
                  { label: "API key verified", done: connected },
                  { label: "Vendor /setup received — room types", done: setupReceived && roomTypes.length > 0 },
                  { label: "Vendor /setup received — rate plans", done: setupReceived && ratePlans.length > 0 },
                  { label: "Room type mapping saved", done: roomsDone },
                  { label: "Rate plan mapping saved", done: plansDone },
                  { label: `${syncDays}-day booking history synced`, done: !!syncResult },
                  { label: `History sync includes rate_info validation${syncValidation?.withoutRateInfo > 0 ? ` (⚠ ${syncValidation.withoutRateInfo} missing)` : ""}`, done: !!syncValidation && (syncValidation.withoutRateInfo === 0) },
                  { label: `Unmapped room fallback policy set (${unmappedPolicy})`, done: !!unmappedPolicy },
                ].map((item) => (
                  <div key={item.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 7 }}>
                    <span style={{ fontSize: 15, color: item.done ? "#16a34a" : "#cbd5e1", fontWeight: 800, flexShrink: 0 }}>{item.done ? "✓" : "○"}</span>
                    <span style={{ fontSize: 13, color: item.done ? "#166534" : "#64748b", fontWeight: item.done ? 700 : 500 }}>{item.label}</span>
                  </div>
                ))}
              </div>

              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                <button className="dp-btn dp-btn-ghost" onClick={() => setStep(3)}>← Back</button>
                <button className="dp-btn dp-btn-primary" onClick={handleGoLive} disabled={saving}>
                  {saving ? <><span className="dp-spin">⟳</span> Saving...</> : "🚀 Save & Go Live"}
                </button>
              </div>
            </Card>
          )}
        </div>
      )}

      {mode === "MANUAL" && (
        <Card style={{ padding: "28px 32px" }}>
          <SectionTitle title="Manual Occupancy" sub="No booking system. Set a fixed occupancy % used by AI pricing decisions across all dates." />
          <div style={{ maxWidth: 560 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
              <label className="dp-label">Default Occupancy Rate</label>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 30, fontWeight: 800, color: "#d97706" }}>{manualOcc}%</span>
            </div>
            <input type="range" min={0} max={100} value={manualOcc} onChange={(e) => setManualOcc(Number(e.target.value))} style={{ width: "100%", marginBottom: 8, accentColor: "#2563eb", height: 6 }} />
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 16, fontSize: 11, color: "#94a3b8" }}><span>0%</span><span>50%</span><span>100%</span></div>
            <div style={{ padding: "13px 16px", borderRadius: 10, marginBottom: 16, background: manualOcc >= 80 ? "#f0fdf4" : manualOcc >= 50 ? "#fffbeb" : "#fef2f2", border: `1px solid ${manualOcc >= 80 ? "#86efac" : manualOcc >= 50 ? "#fde68a" : "#fca5a5"}` }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: manualOcc >= 80 ? "#15803d" : manualOcc >= 50 ? "#b45309" : "#b91c1c", marginBottom: 3 }}>
                {manualOcc >= 80 ? "🟢 High demand — AI will apply premium" : manualOcc >= 50 ? "🟡 Moderate — balanced pricing" : "🔴 Low demand — AI may reduce markup"}
              </div>
              <div style={{ fontSize: 12, color: "#475569", fontWeight: 500 }}>Upgrade to 3rd-party integration or native PMS for per-date accuracy and realised-rate benchmarking.</div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <button className="dp-btn dp-btn-outline" onClick={() => handleSaveMode("MANUAL", { manual_occupancy_pct: Number(manualOcc) })} disabled={savingMode}>
              {savingMode ? <><span className="dp-spin">⟳</span> Saving mode...</> : "Save Manual Mode"}
            </button>
            <button className="dp-btn dp-btn-primary" onClick={handleSaveManual} disabled={saving}>
              {saving ? <><span className="dp-spin">⟳</span> Saving...</> : "Save Settings"}
            </button>
          </div>
        </Card>
      )}
    </div>
  );
}
